// SPDX-License-Identifier: GPL-3.0-or-later

/** Cache-only book maintenance after generic refresh, independent of every open PDF viewer. */

import { listMultikeySourceBooks } from "./multikey-source-cache";
import {
  claimMultikeySourceInventory,
  loadMultikeyIndex,
  prefetchMultikeyRanges,
  verifyCachedMultikeyCanonical,
  type MultikeyResourceOptions,
} from "./multikey-resources";

let refreshInFlight: Promise<void> | undefined;

/**
 * Refreshes configured cached books, claiming editions only after cached canonical verification.
 * Concurrent messages coalesce; a busy cross-tab lock skips duplicate maintenance rather than queues it.
 * Without Web Locks browsers skip maintenance, retaining verified online reading support.
 */
export function refreshMultikeySources(
  signal: AbortSignal,
  options: MultikeyResourceOptions = {},
): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  const run = async () => {
    signal.throwIfAborted();
    let storage = options.cacheStorage;
    try {
      storage ??= globalThis.caches;
    } catch {
      return;
    }
    if (!storage?.open) return;
    const work = async () => {
      for (const book of await listMultikeySourceBooks(storage, signal)) {
        signal.throwIfAborted();
        try {
          const resourceOptions: MultikeyResourceOptions = {
            ...options,
            cacheStorage: storage,
            offline: false,
            canonicalUrl: book.canonicalUrl,
            deferInventoryClaim: true,
            inventory: undefined,
          };
          const { index, previousIndex } = await loadMultikeyIndex(
            book.indexUrl,
            signal,
            resourceOptions,
          );
          await verifyCachedMultikeyCanonical(book.canonicalUrl, index, signal, resourceOptions);
          signal.throwIfAborted();
          resourceOptions.inventory = await claimMultikeySourceInventory(
            index,
            previousIndex,
            signal,
            resourceOptions,
          );
          // Never let unavailable persistence turn background maintenance into unleased writes.
          if (!resourceOptions.inventory) continue;
          await prefetchMultikeyRanges(index, signal, () => {}, resourceOptions);
        } catch {
          signal.throwIfAborted();
          // Partial deployment, offline/quota failure or a broken book must preserve other books.
        }
      }
    };
    if (globalThis.navigator?.locks) {
      await navigator.locks.request("sb-multikey-source-refresh-v2", { ifAvailable: true }, lock =>
        lock ? work() : undefined,
      );
    } else if (typeof window === "undefined") {
      await work();
    }
  };
  refreshInFlight = run().finally(() => {
    refreshInFlight = undefined;
  });
  return refreshInFlight;
}
